import { Box, CircularProgress, List, ListItem, SvgIconProps, Typography } from "@mui/material";
import { Circle, CircleOutlined } from "@mui/icons-material";
import { ICampaign } from "../../App/Typings/Campaigns";
import { CampaignActiveStatus, CampaignTypes } from "../../App/Enums/CampaignTypes";
import { getCampaignActiveStatus } from "../../ManageCampaigns/State/ManageCampaignsState";

export const CampaignDetails = ({ campaign, isFetching }: { campaign: ICampaign; isFetching: boolean }) => {
  const campaignDataList = [
    {
      title: "Total Captures:",
      data: isFetching ? <CircularProgress size={20} /> : campaign.responseCount,
    },
    {
      title: "Type:",
      data: getCampaignTypeDisplay(campaign.campaignType),
    },
    {
      title: "Duration:",
      data: getDisplayDuration(campaign),
    },
    {
      title: "Status:",
      data: getCampaignStatusDisplay(campaign),
    },
  ];

  return (
    <List
      data-test="campaign-information"
      sx={{
        width: 1,
        justifyContent: "center",
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr 1fr",
          sm: "repeat(4, max-content)",
          md: "max-content",
          lg: "repeat(2, max-content)",
          xl: "repeat(4, max-content)",
        },
        columnGap: { xs: 1, sm: 4, xl: 8 },
        rowGap: { xs: 1, sm: 2 },
      }}
    >
      {campaignDataList.map((data) => (
        <ListItem key={data.title} sx={{ display: "block", p: 0 }}>
          <Typography sx={{ fontWeight: "bold", mb: "0.2em" }}>{data.title}</Typography>
          <Typography>{data.data}</Typography>
        </ListItem>
      ))}
    </List>
  );
};

export function getDisplayDuration(campaign: ICampaign) {
  const startDateDisplay =
    campaign.campaignType === CampaignTypes.Form || campaign.campaignType === CampaignTypes.Event
      ? campaign.startDate.toLocaleDateString()
      : campaign.createdDate.toLocaleDateString();
  const endDateDisplay = campaign.endDate.toLocaleDateString();
  return campaign.ongoing ? "Ongoing" : `${startDateDisplay} - ${endDateDisplay}`;
}

function getCampaignStatusDisplay(campaign: ICampaign) {
  const activeStatus = getCampaignActiveStatus(campaign);
  let iconColor = "primary" as SvgIconProps["color"];
  if (activeStatus === CampaignActiveStatus.Scheduled) {
    iconColor = "warning";
  } else if (activeStatus === CampaignActiveStatus.Inactive) {
    iconColor = "disabled";
  }
  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {activeStatus}
      {activeStatus === CampaignActiveStatus.Active || activeStatus === CampaignActiveStatus.Scheduled ? (
        <Circle color={iconColor} fontSize="small" />
      ) : (
        <CircleOutlined color={iconColor} fontSize="small" />
      )}
    </Box>
  );
}

export const getCampaignTypeDisplay = (campaignType: CampaignTypes) => {
  // Regex a string with spaces between capitalized words, e.g. "Job Alerts" instead of "JobAlerts"
  return campaignType.replace(/([A-Z])/g, " $1").trim();
};
